import "@/dayjsConfig";
import "@/domTranslationGuard";
import {i18n} from "@/i18n";
import "@/index.css";
import "@/languageConfig";
import clsxm from "@/lib/clsxm";
import {PermissionProvider} from "@/permissions";
import SharedPostHogProvider from "@/posthog";
import {queryClient} from "@/queryClient";
import {routeTree} from "@/routeTree.gen";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import {ModalProvider} from "@/store/modalContext";
import type {TSettingsModalState} from "@/store/types";
import {stripePromise} from "@/stripe";
import {loadHubSpotTrackingScript} from "@/utils/hubspot";
import {GoogleOAuthProvider} from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import {Elements} from "@stripe/react-stripe-js";
import "@tanstack/react-query";
import {QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {createRouter, RouterProvider} from "@tanstack/react-router";
import {getDefaultStore} from "jotai";
import "rc-slider/assets/index.css";
import {createRoot} from "react-dom/client";
import {I18nextProvider} from "react-i18next";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import {IntercomProvider} from "react-use-intercom";

Sentry.init({
  dsn:
    import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true"
      ? import.meta.env.VITE_APP_SENTRY_DSN_PROD
      : import.meta.env.VITE_APP_SENTRY_DSN_DEV,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  enabled: process.env.NODE_ENV === "production",
  sendDefaultPii: true,
});

loadHubSpotTrackingScript();

const router = createRouter({routeTree, scrollRestoration: true});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

declare module "@tanstack/history" {
  interface HistoryState {
    settingsMask?: TSettingsModalState;
  }
}

const reloadedSettingsModal = router.latestLocation.state.settingsMask;
if (reloadedSettingsModal) getDefaultStore().set(settingsModalAtom, reloadedSettingsModal);

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  createRoot(rootElement).render(
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        {!import.meta.env.VITE_IS_E2E && (
          <ReactQueryDevtools initialIsOpen={true} buttonPosition="bottom-left" position="right" />
        )}
        <ToastContainer
          icon={false}
          position="bottom-center"
          autoClose={5000}
          hideProgressBar
          closeOnClick
          pauseOnHover={false}
          className="z-99999 md:w-7/12"
          draggable
          closeButton={false}
          toastClassName={context =>
            clsxm(
              "rounded-lg border p-3.5 m-2 w-full",
              context?.type === "error"
                ? "bg-danger-light-red text-danger-medium-red border-danger-medium-red"
                : "border-success-medium-green bg-success-light-green text-success-medium-green",
            )
          }
        />
        <SharedPostHogProvider enabled={import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true"}>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_OAUTH_CLIENT_ID}>
            <Elements stripe={stripePromise}>
              <PermissionProvider>
                <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
                  <ModalProvider>
                    <RouterProvider router={router} />
                  </ModalProvider>
                </IntercomProvider>
              </PermissionProvider>
            </Elements>
          </GoogleOAuthProvider>
        </SharedPostHogProvider>
      </QueryClientProvider>
    </I18nextProvider>,
  );
}
